import { useEffect, useMemo, useCallback } from "react";
import useTextFilter from "../../../hooks/useTextFilter";
import useLang from "../../../hooks/useLang";
import useFeatures from "./useFeatures";
import useModifierModal from "../../forms/hooks/useModifierModal";
import useFeatureBlock from "./useFeatureBlock";
import { usePostFeatureMutation } from "../featuresSlice";
import useHasAccess from "../../../hooks/useHasAccess";

const useFeaturesList = () => {
  const hasAccess = useHasAccess({ apiCall: "features.GET" });
  const lang = useLang();
  const features = useFeatures();
  const { filteredData, ...textFilterAttribs } = useTextFilter({
    data: features?.data,
    id: ["name", "description"],
    label: lang("common.search"),
  });
  const { formAttribs, formActions } = useModifierModal({
    mutationHook: usePostFeatureMutation,
    formConfigHook: useFeatureBlock,
    navigateOnSuccess: (response) => `/features/${response?.data?.feature_id}`,
  });

  useEffect(() => {
    features?.refetch();
    // eslint-disable-next-line
  }, []);

  const linkToFeature = useCallback((rowData) => `/features/${rowData.id}`, []);
  const warning = useCallback(
    (rowData) =>
      !(Array.isArray(rowData.conclusions) && rowData.conclusions.length > 0),
    []
  );
  const isLoading = useCallback((rowData) => rowData.id.includes("tmp."), []);

  const header = useMemo(
    () => ({
      title: features.isLoading
        ? lang("features.features_loading")
        : lang("features.title"),
      buttons: {
        caption: lang("common.create"),
        icon: "plus",
        title: lang("features.create"),
        type: "modifier-modal",
        mutation: "features.POST",
        formAttribs,
        ...formActions,
      },
    }),
    [features.isLoading, lang, formAttribs, formActions]
  );

  const tableConfig = useMemo(
    () => [
      {
        id: "name",
        width: 72,
        link: linkToFeature,
        warning,
        isLoading,
      },
      {
        id: "description",
        width: 192,
        grow: true,
      },
    ],
    [linkToFeature, warning, isLoading]
  );

  const items = useMemo(
    () => [
      {
        id: "featuresTable",
        type: "table",
        textFilter: textFilterAttribs,
        data: filteredData,
        config: tableConfig,
      },
    ],
    [textFilterAttribs, filteredData, tableConfig]
  );

  const page = useMemo(
    () => ({
      hasAccess,
      header,
      paging: { ...features?.pagination },
      items,
    }),
    [hasAccess, header, features?.pagination, items]
  );

  return page;
};

export default useFeaturesList;
